import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AdNetworkConfig {
  enabled: boolean;
  rewardedBlockId?: string;
  interstitialBlockId?: string;
  zoneId?: string;
  adId?: number; // Onclicka ad ID
  publisherId?: string;
  appId?: string;
  pubId?: string;
  fullscreenPlacementId?: string;
  bannerPlacementId?: string;
  rewardAmount?: number;
  interstitialReward?: number;
  videoReward?: number;
  bannerReward?: number;
  popupReward?: number;
  pushReward?: number;
  dailyLimit?: number;
}

interface WithdrawalConfig {
  enabled: boolean;
  minTonAmount: number;
  platformFeePercent: number;
  pointsToTonRate: number;
  autoProcessEnabled: boolean;
}

interface ReferralConfig {
  enabled: boolean;
  level1Percent: number;
  level2Percent: number;
  level3Percent: number;
}

interface FaucetConfig {
  hourlyClaimMin: number;
  hourlyClaimMax: number;
  hourlyClaimCooldown: number;
  autoFaucetInterval: number;
  autoFaucetBaseReward: number;
  maxDailyAutoCycles: number;
  energyRegenRate: number;
  maxEnergy: number;
  energyPerAd: number;
}

interface ConfigState {
  // Ad networks
  adsEnabled: boolean;
  dailyAdLimit: number;
  adCooldown: number;
  adsgram: AdNetworkConfig;
  onclicka: AdNetworkConfig;
  adextra: AdNetworkConfig;
  gigapub: AdNetworkConfig;
  richads: AdNetworkConfig;
  monetag: AdNetworkConfig;
  
  // Withdrawal
  withdrawal: WithdrawalConfig;
  
  // Referral
  referral: ReferralConfig;
  
  // Faucet
  faucet: FaucetConfig;
  
  // Games
  wheelEnabled: boolean;
  tapEnabled: boolean;
  
  // System
  maintenanceMode: boolean;
  maintenanceMessage: string;
  
  // Meta
  lastFetched: number | null;
  isLoading: boolean;
  
  // Actions
  fetchConfig: (forceRefresh?: boolean) => Promise<void>;
  getEnabledAdNetworks: () => string[];
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      // Default values
      adsEnabled: true,
      dailyAdLimit: 50,
      adCooldown: 30,
      adsgram: {
        enabled: true,
        rewardedBlockId: '18951',
        interstitialBlockId: 'int-18952',
        rewardAmount: 20,
        interstitialReward: 15,
        dailyLimit: 20,
      },
      onclicka: {
        enabled: true,
        adId: 6103374,
        bannerId: '403455',
        rewardAmount: 20,
        bannerReward: 25,
        dailyLimit: 25,
      },
      adextra: {
        enabled: true,
        publisherId: '',
        fullscreenPlacementId: '',
        bannerPlacementId: '',
        videoReward: 100,
        bannerReward: 25,
        dailyLimit: 20,
      },
      gigapub: {
        enabled: false,
        publisherId: '',
        rewardAmount: 100,
        dailyLimit: 10,
      },
      monetag: {
        enabled: true,
        zoneId: '10377230',
        rewardAmount: 50,
        dailyLimit: 20,
      },
      richads: {
        enabled: true,
        pubId: '833686',
        appId: '5025',
        pushReward: 50,
        interstitialReward: 80,
        videoReward: 100,
        bannerReward: 30,
        dailyLimit: 20,
      },
      withdrawal: {
        enabled: true,
        minTonAmount: 0.5,
        platformFeePercent: 5,
        pointsToTonRate: 100000,
        autoProcessEnabled: false,
      },
      referral: {
        enabled: true,
        level1Percent: 10,
        level2Percent: 5,
        level3Percent: 2,
      },
      faucet: {
        hourlyClaimMin: 30,
        hourlyClaimMax: 50,
        hourlyClaimCooldown: 3600,
        autoFaucetInterval: 30,
        autoFaucetBaseReward: 10,
        maxDailyAutoCycles: 2880,
        energyRegenRate: 1,
        maxEnergy: 100,
        energyPerAd: 5,
      },
      wheelEnabled: true,
      tapEnabled: true,
      maintenanceMode: false,
      maintenanceMessage: '',
      lastFetched: null,
      isLoading: false,

      fetchConfig: async (forceRefresh = false) => {
        const { lastFetched } = get();
        
        // Cache for 2 minutes unless force refresh
        if (!forceRefresh && lastFetched && Date.now() - lastFetched < 2 * 60 * 1000) {
          return;
        }

        set({ isLoading: true });

        try {
          // Add cache-busting query param to bypass CDN/browser cache
          const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
          const response = await fetch(`${API_URL}/config/public${cacheBuster}`);
          if (!response.ok) throw new Error('Failed to fetch config');
          
          const data = await response.json();
          const config = data.config || {};

          // Check cache version and clear storage if admin triggered cache clear
          const serverCacheVersion = config['system.cacheVersion'] ?? 1;
          const localCacheVersionStr = localStorage.getItem('gemifaucet_cache_version');
          const localCacheVersion = localCacheVersionStr ? parseInt(localCacheVersionStr, 10) : 0;
          
          // Only clear cache if:
          // 1. Server version is higher than local version
          // 2. Local version exists (not first-time user)
          // 3. We haven't just reloaded (prevent infinite loop)
          const justReloaded = sessionStorage.getItem('gemifaucet_cache_clearing');
          
          if (serverCacheVersion > localCacheVersion && localCacheVersion > 0 && !justReloaded) {
            console.log(`[Cache] Server version ${serverCacheVersion} > local version ${localCacheVersion} - clearing user cache`);
            
            // Mark that we're about to reload (prevents infinite loop)
            sessionStorage.setItem('gemifaucet_cache_clearing', 'true');
            
            // Show loading indicator before reload
            const loadingOverlay = document.createElement('div');
            loadingOverlay.id = 'cache-clear-overlay';
            loadingOverlay.innerHTML = `
              <div style="position:fixed;inset:0;background:#1a1a2e;display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:99999;">
                <div style="width:48px;height:48px;border:3px solid rgba(255,255,255,0.1);border-top-color:#0ea5e9;border-radius:50%;animation:spin 1s linear infinite;"></div>
                <p style="color:white;margin-top:16px;font-family:system-ui,sans-serif;">Updating app...</p>
              </div>
              <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
            `;
            document.body.appendChild(loadingOverlay);
            
            // Clear all user-related localStorage (preserve admin data and welcome state)
            const keysToRemove = [
              'gemifaucet-storage',    // Main app store
              'gemifaucet-config',     // Config store
              'auth_token',            // Auth token
              // 'gemifaucet_welcomed' - PRESERVED: Don't show welcome screen again
              // 'gemifaucet_channel_card_hidden' - PRESERVED: User preference
            ];
            
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Save new cache version BEFORE reload
            localStorage.setItem('gemifaucet_cache_version', String(serverCacheVersion));
            
            // Small delay to show the loading UI, then reload
            setTimeout(() => {
              window.location.reload();
            }, 300);
            return;
          }
          
          // Clear the reload flag after successful config load
          sessionStorage.removeItem('gemifaucet_cache_clearing');
          
          // Update local cache version (for first-time users or normal operation)
          localStorage.setItem('gemifaucet_cache_version', String(serverCacheVersion));

          set({
            adsEnabled: config['ads.enabled'] ?? true,
            dailyAdLimit: config['ads.dailyLimitPerUser'] ?? 50,
            adCooldown: config['ads.cooldownBetweenAds'] ?? 30,
            adsgram: {
              enabled: config['ads.adsgram.enabled'] ?? true,
              rewardedBlockId: config['ads.adsgram.rewardedBlockId'] || '18951',
              interstitialBlockId: config['ads.adsgram.interstitialBlockId'] || 'int-18952',
              rewardAmount: config['ads.adsgram.rewardAmount'] ?? 20,
              interstitialReward: config['ads.adsgram.interstitialReward'] ?? 15,
              dailyLimit: config['ads.adsgram.dailyLimit'] ?? 20,
            },
            onclicka: {
              enabled: config['ads.onclicka.enabled'] ?? true,
              adId: config['ads.onclicka.adId'] || 6103374,
              rewardAmount: config['ads.onclicka.rewardAmount'] ?? 20,
              dailyLimit: config['ads.onclicka.dailyLimit'] ?? 25,
            },
            adextra: {
              enabled: config['ads.adextra.enabled'] ?? true,
              publisherId: config['ads.adextra.publisherId'] || '',
              fullscreenPlacementId: config['ads.adextra.fullscreenPlacementId'] || '',
              bannerPlacementId: config['ads.adextra.bannerPlacementId'] || '',
              videoReward: config['ads.adextra.videoReward'] ?? 100,
              bannerReward: config['ads.adextra.bannerReward'] ?? 25,
              dailyLimit: config['ads.adextra.dailyLimit'] ?? 20,
            },
            gigapub: {
              enabled: config['ads.gigapub.enabled'] ?? false,
              publisherId: config['ads.gigapub.publisherId'] || '',
              rewardAmount: config['ads.gigapub.rewardAmount'] ?? 100,
              dailyLimit: config['ads.gigapub.dailyLimit'] ?? 10,
            },
            richads: {
              enabled: config['ads.richads.enabled'] ?? true,
              pubId: config['ads.richads.pubId'] || '833686',
              appId: config['ads.richads.appId'] || '5025',
              pushReward: config['ads.richads.pushReward'] ?? 50,
              interstitialReward: config['ads.richads.interstitialReward'] ?? 80,
              videoReward: config['ads.richads.videoReward'] ?? 100,
              bannerReward: config['ads.richads.bannerReward'] ?? 30,
              dailyLimit: config['ads.richads.dailyLimit'] ?? 20,
            },
            monetag: {
              enabled: config['ads.monetag.enabled'] ?? true,
              zoneId: config['ads.monetag.zoneId'] || '10377230',
              rewardAmount: config['ads.monetag.rewardAmount'] ?? 50,
              dailyLimit: config['ads.monetag.dailyLimit'] ?? 20,
            },
            withdrawal: {
              enabled: config['withdrawal.enabled'] ?? true,
              minTonAmount: config['withdrawal.minTonAmount'] ?? 0.5,
              platformFeePercent: config['withdrawal.platformFeePercent'] ?? 5,
              pointsToTonRate: config['withdrawal.pointsToTonRate'] ?? 100000,
              autoProcessEnabled: config['withdrawal.autoProcessEnabled'] ?? false,
            },
            referral: {
              enabled: config['referral.enabled'] ?? true,
              level1Percent: config['referral.level1Percent'] ?? 10,
              level2Percent: config['referral.level2Percent'] ?? 5,
              level3Percent: config['referral.level3Percent'] ?? 2,
            },
            faucet: {
              hourlyClaimMin: config['faucet.hourlyClaimMin'] ?? 30,
              hourlyClaimMax: config['faucet.hourlyClaimMax'] ?? 50,
              hourlyClaimCooldown: config['faucet.hourlyClaimCooldown'] ?? 3600,
              autoFaucetInterval: config['faucet.autoFaucetInterval'] ?? 30,
              autoFaucetBaseReward: config['faucet.autoFaucetBaseReward'] ?? 10,
              maxDailyAutoCycles: config['faucet.maxDailyAutoCycles'] ?? 2880,
              energyRegenRate: config['faucet.energyRegenRate'] ?? 1,
              maxEnergy: config['faucet.maxEnergy'] ?? 100,
              energyPerAd: config['faucet.energyPerAd'] ?? 5,
            },
            wheelEnabled: config['gamification.wheelEnabled'] ?? true,
            tapEnabled: config['gamification.tapEnabled'] ?? true,
            maintenanceMode: config['system.maintenanceMode'] ?? false,
            maintenanceMessage: config['system.maintenanceMessage'] || '',
            lastFetched: Date.now(),
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to fetch config:', error);
          set({ isLoading: false });
        }
      },

      getEnabledAdNetworks: () => {
        const state = get();
        const enabled: string[] = [];
        
        if (!state.adsEnabled) return enabled;
        
        if (state.adsgram.enabled) enabled.push('adsgram');
        if (state.onclicka.enabled) enabled.push('onclicka');
        if (state.adextra.enabled) enabled.push('adextra');
        if (state.gigapub.enabled) enabled.push('gigapub');
        if (state.richads.enabled) enabled.push('richads');
        if (state.monetag.enabled) enabled.push('monetag');
        
        return enabled;
      },
    }),
    {
      name: 'gemifaucet-config',
      partialize: (state) => ({
        adsEnabled: state.adsEnabled,
        adsgram: state.adsgram,
        onclicka: state.onclicka,
        adextra: state.adextra,
        gigapub: state.gigapub,
        richads: state.richads,
        monetag: state.monetag,
        withdrawal: state.withdrawal,
        referral: state.referral,
        faucet: state.faucet,
        wheelEnabled: state.wheelEnabled,
        tapEnabled: state.tapEnabled,
        lastFetched: state.lastFetched,
      }),
    }
  )
);
